import React, { useEffect } from 'react';
import {
  Banner,
  Button,
  CondensedMedium,
  Gutter,
  Layout,
  Loader,
  NotFoundError,
  useApiWithErrors,
} from '@axiom/ui';
import type { Candidate, DsMatchSummary, Opportunity } from '@axiom/validation';

import { MatchSummaryApi } from '../../api/match-summary-api';
import { useInterval } from '../../hooks/useInterval';

import { OpportunityTalentMatchSummaryReceived } from './OpportunityTalentMatchSummaryReceived';

const POLL_INTERVAL = 5000;
const COOLDOWN_MS = 10 * 60 * 1000;

export const OpportunityTalentMatchSummaryFitMetrics = ({
  name = 'MATCH_SUMMARY',
  opportunityId,
  candidateId,
}: {
  name?: string;
  opportunityId: Opportunity['id'];
  candidateId: Candidate['id'];
}) => {
  const { stateChangeTrigger } = useInterval(POLL_INTERVAL);

  const responses = useApiWithErrors(
    MatchSummaryApi.readMatchSummary({ opportunityId, candidateId })
  );

  const [matchSummaryResponse] = responses;

  if (
    matchSummaryResponse instanceof Error &&
    !(matchSummaryResponse instanceof NotFoundError)
  ) {
    throw matchSummaryResponse;
  }

  const dsMatchSummary: DsMatchSummary | null =
    matchSummaryResponse instanceof NotFoundError
      ? {
          status: 'Failed',
          data: null,
          requestedAt: null,
          receivedAt: null,
          failedAt: null,
          error: null,
        }
      : matchSummaryResponse.data.dsMatchSummary;
  const status = dsMatchSummary?.status ?? null;
  const isPolling = status === 'Requested';
  const isCooldownActive = (() => {
    return dsMatchSummary?.receivedAt
      ? Date.now() - new Date(dsMatchSummary.receivedAt).getTime() < COOLDOWN_MS
      : false;
  })();

  useEffect(() => {
    if (isPolling) {
      void MatchSummaryApi.refreshMatchSummary({ opportunityId, candidateId });
    }
  }, [stateChangeTrigger]);

  const handleRequest = async () => {
    await MatchSummaryApi.createMatchSummaryRequest({
      opportunityId,
      candidateId,
    });
    await MatchSummaryApi.refreshMatchSummary({ opportunityId, candidateId });
  };

  const renderContent = () => {
    if (!dsMatchSummary || status === null) {
      return (
        <CondensedMedium name="NO_SUMMARY_MESSAGE">
          Click below to fetch summary data
        </CondensedMedium>
      );
    }

    if (status === 'Requested') {
      return <Loader type="bars" name="MATCH_SUMMARY_LOADER" />;
    }

    if (status === 'Failed' || !dsMatchSummary.data?.fitDecision) {
      return (
        <Layout direction="vertical" verticalGutter="8px">
          <Banner name="ERROR_BANNER" type="error">
            No AI summary available.
          </Banner>
          <CondensedMedium name="FAILED_MESSAGE">
            Click below to fetch summary data
          </CondensedMedium>
        </Layout>
      );
    }

    return <OpportunityTalentMatchSummaryReceived data={dsMatchSummary.data} />;
  };

  return (
    <div data-test={name}>
      {renderContent()}
      <Gutter top="16px">
        <Button
          name="REQUEST_MATCH_SUMMARY"
          disabled={isPolling || isCooldownActive}
          onClick={() => {
            void handleRequest();
          }}
        >
          Load AI Summary Data
        </Button>
      </Gutter>
    </div>
  );
};
